/* ─────────────────────────────────────────────────────────────────
   MLW Quiz Email Capture
   ─────────────────────────────────────────────────────────────────
   Renders: heading + subtext + email input + optional name input +
           primary CTA + optional skip link + privacy microcopy.

   Three modes:
     • "required"   — no skip, submit disabled until email valid (MLW)
     • "optional"   — skip link visible (TMT)
     • "skippable"  — auto-skip path: if hasPrefilledEmail is true the
                      engine bypasses this stage entirely; otherwise
                      behaves like "optional".

   Layout matches the JSX prototype: content inside a white card
   (background #fff, border-radius 16, padding 40px 24px) with the
   email input + submit button rendered side-by-side as a flex row,
   and a back button rendered OUTSIDE and BELOW the card.

   Enter key submits when the form is valid. On submit:
     1. The onSubmit({ email, name }) callback fires.
     2. A `mlw-quiz:email-submitted` CustomEvent is dispatched on
        `document` with detail = { email, firstName } so external
        integrations (e.g. snippets/quiz-klaviyo-init.liquid) can
        react without touching the main quiz section.

   Back navigation: the back button carries data-quiz-action="back"
   and data-quiz-section="<sectionId>" so the section's existing
   click delegation (in sections/mlw-quiz.liquid) handles it. The
   sectionId is auto-discovered from the closest [data-section-id]
   ancestor at mount time, so no new wiring is required.

   API
     MLWQuizEmailCapture.mount(container, {
       mode, heading, subtext, nameEnabled, namePlaceholder,
       emailPlaceholder, buttonLabel, skipLabel, privacyText,
       backLabel, showBack,
       onSubmit, onSkip,
     })
   ───────────────────────────────────────────────────────────────── */

(function (window) {
  "use strict";

  /* Token fallbacks used when mlw-base.css / quiz-tokens.css fail
     to load ahead of this script. Hex values match the JSX. */
  var HEX_ACCENT       = "#8b7fc7";
  var HEX_MUTED        = "#6b6580";
  var HEX_DEEP_PURPLE  = "#1a1728";
  var HEX_BODY_TEXT    = "#2d2a3e";
  var HEX_LIGHT_TINT   = "#ddd6f3";
  var HEX_LIGHTEST     = "#f0eef8";
  var HEX_WHITE        = "#ffffff";

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        if (k === "class") node.className = attrs[k];
        else if (k.indexOf("data-") === 0 || k.indexOf("aria-") === 0) node.setAttribute(k, attrs[k]);
        else if (k in node) node[k] = attrs[k];
        else node.setAttribute(k, attrs[k]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        if (typeof c === "string") node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      }
    }
    return node;
  }

  /* Defensive: we only accept strings with a single "@" and a "." after it.
     The backend is the real source of truth for delivery validity. */
  function isLikelyEmail(s) {
    if (!s || typeof s !== "string") return false;
    var trimmed = s.trim();
    var at = trimmed.indexOf("@");
    if (at < 1) return false;
    var dot = trimmed.indexOf(".", at);
    return dot > at + 1 && dot < trimmed.length - 1;
  }

  function mount(container, options) {
    options = options || {};
    var mode = options.mode || "optional";
    var heading = options.heading || "";
    var subtext = options.subtext || "";
    var nameEnabled = options.nameEnabled === true;
    var namePlaceholder = options.namePlaceholder || "First name";
    var emailPlaceholder = options.emailPlaceholder || "Email address";
    var buttonLabel = options.buttonLabel || "Continue";
    var skipLabel = options.skipLabel || "Skip";
    var privacyText = options.privacyText || "";
    var backLabel = options.backLabel || "← Back";
    var showBack = options.showBack !== false;
    var onSubmit = typeof options.onSubmit === "function" ? options.onSubmit : null;
    var onSkip = typeof options.onSkip === "function" ? options.onSkip : null;

    /* Auto-discover section id so the back button can hook into
       the section's existing [data-quiz-action="back"] delegation
       without the caller having to pass it explicitly. */
    var sectionId = "";
    var sectionHost = container.closest ? container.closest("[data-section-id]") : null;
    if (sectionHost) sectionId = sectionHost.getAttribute("data-section-id") || "";

    /* ── Outer wrapper ────────────────────────────────────────── */
    var wrap = el("div", {
      class: "quiz-email-capture",
      style:
        "padding:24px 20px 40px;max-width:560px;margin:0 auto;width:100%;" +
        "box-sizing:border-box;",
    });

    /* ── White card ───────────────────────────────────────────── */
    var card = el("div", {
      class: "quiz-email-capture__card",
      style:
        "background:var(--quiz-card-bg, " + HEX_WHITE + ");" +
        "border-radius:16px;padding:40px 24px;text-align:center;" +
        "box-sizing:border-box;",
    });

    if (heading) {
      card.appendChild(el("h2", {
        class: "quiz-email-capture__heading",
        style:
          "font-family:var(--quiz-font-heading);" +
          "font-size:clamp(22px,2.6vw,28px);font-weight:700;" +
          "color:var(--quiz-text-dark, " + HEX_DEEP_PURPLE + ");" +
          "line-height:1.15;margin:0 0 10px;text-align:center;",
      }, [heading]));
    }

    if (subtext) {
      card.appendChild(el("p", {
        class: "quiz-email-capture__subtext",
        style:
          "font-family:var(--quiz-font-body);font-size:14px;font-weight:400;" +
          "color:var(--quiz-text-med, " + HEX_MUTED + ");line-height:1.65;" +
          "margin:0 0 28px;text-align:center;",
      }, [subtext]));
    }

    /* ── Form — name stacked (if enabled) above the email/submit row ── */
    var form = el("form", {
      class: "quiz-email-capture__form",
      style: "display:flex;flex-direction:column;gap:10px;width:100%;",
      noValidate: true,
    });

    var nameInput = null;
    if (nameEnabled) {
      nameInput = el("input", {
        type: "text",
        class: "quiz-email-capture__name",
        name: "quiz_name",
        placeholder: namePlaceholder,
        autocomplete: "given-name",
        style:
          "width:100%;max-width:420px;margin:0 auto;box-sizing:border-box;" +
          "padding:14px 16px;" +
          "border:1px solid var(--quiz-progress-bg, " + HEX_LIGHT_TINT + ");" +
          "border-radius:10px;" +
          "background:" + HEX_LIGHTEST + ";" +
          "font-family:var(--quiz-font-body);font-size:15px;font-weight:400;" +
          "color:var(--quiz-text-dark, " + HEX_BODY_TEXT + ");outline:none;" +
          "transition:border-color var(--quiz-transition-speed, 0.25s) ease;",
      });
      nameInput.addEventListener("focus", function () { nameInput.style.borderColor = "var(--quiz-primary, " + HEX_ACCENT + ")"; });
      nameInput.addEventListener("blur", function () { nameInput.style.borderColor = "var(--quiz-progress-bg, " + HEX_LIGHT_TINT + ")"; });
      form.appendChild(nameInput);
    }

    /* Email + Submit row — flex row, wraps on narrow screens. */
    var row = el("div", {
      class: "quiz-email-capture__row",
      style:
        "display:flex;gap:10px;max-width:420px;margin:0 auto;" +
        "flex-wrap:wrap;width:100%;",
    });

    var emailInput = el("input", {
      type: "email",
      class: "quiz-email-capture__email",
      name: "quiz_email",
      placeholder: emailPlaceholder,
      autocomplete: "email",
      required: true,
      style:
        "flex:1 1 200px;box-sizing:border-box;" +
        "padding:14px 16px;" +
        "border:1px solid var(--quiz-progress-bg, " + HEX_LIGHT_TINT + ");" +
        "border-radius:10px;" +
        "background:" + HEX_LIGHTEST + ";" +
        "font-family:var(--quiz-font-body);font-size:15px;font-weight:400;" +
        "color:var(--quiz-text-dark, " + HEX_BODY_TEXT + ");outline:none;" +
        "transition:border-color var(--quiz-transition-speed, 0.25s) ease;",
    });
    emailInput.addEventListener("focus", function () { emailInput.style.borderColor = "var(--quiz-primary, " + HEX_ACCENT + ")"; });
    emailInput.addEventListener("blur", function () { emailInput.style.borderColor = "var(--quiz-progress-bg, " + HEX_LIGHT_TINT + ")"; });
    row.appendChild(emailInput);

    var submitBtn = el("button", {
      type: "submit",
      class: "quiz-email-capture__submit",
      style:
        "flex-shrink:0;padding:14px 24px;border:none;border-radius:10px;" +
        "cursor:pointer;" +
        "font-family:var(--quiz-font-body);font-size:15px;font-weight:700;" +
        "letter-spacing:0;" +
        "transition:background var(--quiz-transition-speed, 0.25s) ease," +
                   "color var(--quiz-transition-speed, 0.25s) ease;",
    }, [buttonLabel]);
    row.appendChild(submitBtn);
    form.appendChild(row);

    /* Style submit in enabled vs disabled states per the JSX:
         enabled  → accent bg, white text
         disabled → light-tint bg, muted text */
    function applyEnabledStyles(enabled) {
      if (enabled) {
        submitBtn.style.background = "var(--quiz-primary, " + HEX_ACCENT + ")";
        submitBtn.style.color = "#fff";
        submitBtn.style.cursor = "pointer";
      } else {
        submitBtn.style.background = "var(--quiz-progress-bg, " + HEX_LIGHT_TINT + ")";
        submitBtn.style.color = "var(--quiz-text-med, " + HEX_MUTED + ")";
        submitBtn.style.cursor = "not-allowed";
      }
    }

    /* Disabled-until-valid is a hard rule only for "required" mode.
       Other modes let the user submit and let validation bounce. */
    function refreshDisabled() {
      if (mode !== "required") {
        submitBtn.disabled = false;
        applyEnabledStyles(true);
        return;
      }
      var ok = isLikelyEmail(emailInput.value);
      submitBtn.disabled = !ok;
      applyEnabledStyles(ok);
    }
    emailInput.addEventListener("input", refreshDisabled);
    refreshDisabled();

    function handleSubmit(e) {
      if (e) e.preventDefault();
      var email = (emailInput.value || "").trim();
      var name = nameInput ? (nameInput.value || "").trim() : "";
      if (mode === "required" && !isLikelyEmail(email)) {
        emailInput.style.borderColor = "#c44";
        emailInput.focus();
        return;
      }
      if (onSubmit) onSubmit({ email: email, name: name });

      /* Broadcast for decoupled integrations (Klaviyo snippet etc).
         We intentionally fire this AFTER onSubmit so any in-flight
         state mutation (e.g. storing email on the quiz config) has
         already happened and listeners can read from it. */
      try {
        document.dispatchEvent(new CustomEvent("mlw-quiz:email-submitted", {
          detail: { email: email, firstName: name }
        }));
      } catch (_e) { /* old browsers without CustomEvent — ignore */ }
    }

    form.addEventListener("submit", handleSubmit);
    emailInput.addEventListener("keydown", function (e) {
      if (e.key === "Enter") handleSubmit(e);
    });

    card.appendChild(form);

    /* Skip link — visible only on optional/skippable modes. */
    if (mode !== "required" && skipLabel) {
      var skipBtn = el("button", {
        type: "button",
        class: "quiz-email-capture__skip",
        style:
          "margin-top:16px;padding:8px 12px;background:transparent;border:none;" +
          "cursor:pointer;font-family:var(--quiz-font-body);font-size:13px;" +
          "color:var(--quiz-text-light, " + HEX_MUTED + ");text-decoration:underline;" +
          "text-underline-offset:3px;",
      }, [skipLabel]);
      skipBtn.addEventListener("click", function () {
        if (onSkip) onSkip();
      });
      card.appendChild(skipBtn);
    }

    if (privacyText) {
      card.appendChild(el("p", {
        class: "quiz-email-capture__privacy",
        style:
          "margin:16px 0 0;font-size:11px;font-family:var(--quiz-font-body);" +
          "color:var(--quiz-text-med, " + HEX_MUTED + ");line-height:1.65;opacity:0.7;" +
          "max-width:400px;margin-left:auto;margin-right:auto;",
      }, [privacyText]));
    }

    wrap.appendChild(card);

    /* ── Back button — OUTSIDE and BELOW the card ────────────── */
    var backBtn = null;
    if (showBack) {
      var backAttrs = {
        type: "button",
        class: "quiz-email-capture__back",
        "data-quiz-action": "back",
        style:
          "display:block;margin:20px auto 0;padding:8px 16px;" +
          "background:transparent;border:none;cursor:pointer;" +
          "font-family:var(--quiz-font-body);font-size:13px;font-weight:400;" +
          "color:var(--quiz-text-med, " + HEX_MUTED + ");",
      };
      if (sectionId) backAttrs["data-quiz-section"] = sectionId;
      backBtn = el("button", backAttrs, [backLabel]);
      wrap.appendChild(backBtn);
    }

    container.appendChild(wrap);

    return {
      root: wrap,
      card: card,
      emailInput: emailInput,
      nameInput: nameInput,
      submitBtn: submitBtn,
      backBtn: backBtn,
      setValues: function (data) {
        if (!data) return;
        if (emailInput && typeof data.email === "string") emailInput.value = data.email;
        if (nameInput && typeof data.name === "string") nameInput.value = data.name;
        refreshDisabled();
      },
    };
  }

  window.MLWQuizEmailCapture = { mount: mount };
})(window);
